import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil, tap } from 'rxjs';
import { Customer } from 'server/shared/models/customer';
import { DispoUser, PoolerUser } from 'server/shared/models/user';
import { CustomerService } from 'src/app/services/customer.service';
import { UserService } from 'src/app/services/user.service';
import { SubscriptionService } from 'src/app/shared/subscription.service';
import { ToastService } from '../../services/toast/toast.service';
import {
  CreateEmploymentData,
  EmploymentSelectedAvailabilityDialogComponent,
} from './employment-selected-availability-dialog/employment-selected-availability-dialog.component';
import { EmploymentAvailabilityManagementService } from './service/employment-availability-management.service';

@Component({
  selector: 'temp-work-availability-management',
  templateUrl: 'availability-management.html',
  styleUrls: ['availability-management.scss'],
  providers: [EmploymentAvailabilityManagementService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmploymentAvailabilityManagement {
  destroyed$ = new Subject<void>();
  users: PoolerUser[] = [];

  usersTracker?: Tracker.Computation;

  user?: DispoUser;
  dispoUser$ = this.userService.readyDispo$;

  dispoCustomer$ = this.customerService.readyDispoCustomer$;

  loggedInCustomer?: Customer;
  customDateRange: boolean = false;
  rangeStartDate?: Date;
  rangeEndDate?: Date;

  departmentParam$ = this.managementService.departmentParam$;

  dateRangeChips = ['ALL', 'CUSTOM'];

  dispoUserEffect$ = this.dispoUser$.pipe(
    tap((user) => {
      this.user = user;
      if (user?.employmentAvailabilitiesOverviewFilter) {
        this._loadDefaultFilters(user);
      }
    }),
    takeUntil(this.destroyed$),
  );

  dispoCustomerEffect$ = this.dispoCustomer$.pipe(
    tap((customer) => {
      this.loggedInCustomer = customer;
    }),
    takeUntil(this.destroyed$),
  );

  constructor(
    private dialog: MatDialog,
    private router: Router,
    public managementService: EmploymentAvailabilityManagementService,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private userService: UserService,
    private customerService: CustomerService,
    private subscriptionService: SubscriptionService,
  ) {}

  ngOnInit() {
    this.dispoUserEffect$.subscribe();
    this.dispoCustomerEffect$.subscribe();
    this.subscriptionService.organisations.ready$
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  createEmploymentFromAvailability(): void {
    const dialogRef = this.dialog.open(
      EmploymentSelectedAvailabilityDialogComponent,
      {
        data: {
          departmentId: this.route.snapshot.queryParams.department,
          startDate: this.route.snapshot.queryParams.selectedFrom,
          endDate: this.route.snapshot.queryParams.selectedTo,
          certificates: this.route.snapshot.queryParams.certificates,
        },
      },
    );
    dialogRef.afterClosed().subscribe((result: CreateEmploymentData) => {
      if (result) {
        this.router
          .navigate(['temp-work/employments', 'new'], {
            queryParams: result,
          })
          .then();
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.usersTracker?.stop();
  }

  saveDefaultFilter() {
    if (!this.user) return;
    Meteor.call(
      'saveEmploymentAvailabilitiesOverviewFilter',
      this.route.snapshot.queryParams,
    );
    if (!this.user.employmentAvailabilitiesOverviewFilter) {
      this.user.employmentAvailabilitiesOverviewFilter = {};
    }
    this.user.employmentAvailabilitiesOverviewFilter =
      this.route.snapshot.queryParams;
    this.toastService.add({
      type: 'SUCCESS',
      message: 'savedDefaultFilter',
      timeout: 5000,
    });
  }

  private _loadDefaultFilters(user: DispoUser) {
    let defaultValues;
    let filterValues;
    const routeQueryParams = this.route.snapshot.queryParams;
    if (user && user.employmentAvailabilitiesOverviewFilter) {
      if (
        user.employmentAvailabilitiesOverviewFilter &&
        Object.keys(user.employmentAvailabilitiesOverviewFilter)?.length
      ) {
        defaultValues = user.employmentAvailabilitiesOverviewFilter;
      }
    }

    if (Object.keys(routeQueryParams).length !== 0) {
      //query param values
      filterValues = routeQueryParams || defaultValues || {};
    } else if (defaultValues) {
      //user default values
      filterValues = defaultValues;
    } else {
      //origin default values
      filterValues = {};
    }
    let extras: any = {
      relativeTo: this.route,
      queryParams: filterValues,
      replaceUrl: true,
      queryParamsHandling: 'merge',
    };
    this.router.navigate([], extras);
  }
}
